import {appSessionStorage} from "../../../shared/services";

const SELECTED_SHIPPING_METHOD_KEY = 'checkout-shipping-selected-method';

// Mirrors the carrier the cart falls back to, so both show the same method on a first visit.
const DEFAULT_SHIPPING_CARRIER = 'deliveryservice';

const isSameShippingMethod = (method, otherMethod) => {
  return !!method && !!otherMethod
    && method['carrier_code'] === otherMethod['carrier_code']
    && method['method_code'] === otherMethod['method_code'];
};

const initialState = () => {
  return {
    shippingMethod: appSessionStorage.getItem(SELECTED_SHIPPING_METHOD_KEY, {}),
    shippingMethods: [],
    shippingCountries: {},
    loadingShippingMethods: false,
  };
}

const getters = {
  shippingMethod: (state) => state.shippingMethod,
  shippingMethods: (state) => state.shippingMethods,
  shippingCountries: (state) => state.shippingCountries.sort((a, b) => a['full_name_locale'] > b['full_name_locale'] && 1 || -1),
  loadingShippingMethods: (state) => state.loadingShippingMethods,
};

const mutations = {
  SET_SHIPPING_METHOD: (state, payload) => state.shippingMethod = payload,
  SET_SHIPPING_METHODS: (state, payload) => state.shippingMethods = payload,
  SET_SHIPPING_COUNTRIES: (state, payload) => state.shippingCountries = payload,
  SET_LOADING_SHIPPING_METHODS: (state, payload) => state.loadingShippingMethods = payload,
};

const actions = {
  async setShippingMethod({commit, dispatch, rootGetters}, shippingMethod) {
    commit('SET_SHIPPING_METHOD', shippingMethod || {});
    appSessionStorage.setItem(SELECTED_SHIPPING_METHOD_KEY, shippingMethod || {});

    if (!shippingMethod || !shippingMethod['carrier_code'] || !shippingMethod['method_code']) {
      return;
    }

    // Push the choice to the quote right away, so the cart shows it too instead of only after step 3.
    try {
      const [_billingAddress, _shippingAddress] = rootGetters['CheckoutAddress/getAddressData'];

      await this.$solarClient.post('/api/checkout/shipping-information', {
        addressInformation: {
          shippingAddress: _shippingAddress,
          billingAddress: _billingAddress,
          shippingMethodCode: shippingMethod['method_code'],
          shippingCarrierCode: shippingMethod['carrier_code'],
          extensionAttributes: rootGetters['CheckoutGlobal/getExtensionAttributes'],
        }
      });

      await dispatch('CheckoutTotals/fetchCartTotals', null, {root: true});
    } catch (err) {
      //
    }
  },
  async collectShippingMethods({state, rootGetters, commit, dispatch}) {
    let methods = [];

    try {
      const [_billingAddress, _shippingAddress] = rootGetters['CheckoutAddress/getAddressData'];

      commit('SET_LOADING_SHIPPING_METHODS', true);
      const {data} = await this.$solarClient.post('/api/checkout/shipping/collectShippingMethods', {
        address: _shippingAddress,
      });

      methods = Array.isArray(data) ? data : [];
      commit('SET_SHIPPING_METHODS', methods);
    } catch (err) {
      //
    } finally {
      commit('SET_LOADING_SHIPPING_METHODS', false);
    }

    // Runs after the list is rendered, so a failing sync never hides the methods.
    if (methods.length) {
      await dispatch('resolveSelectedShippingMethod', methods);
    }
  },
  /**
   * Re-select from the offered methods, so a method that is no longer available drops out.
   */
  async resolveSelectedShippingMethod({state, commit, dispatch}, methods) {
    const selectedMethod = methods.find((method) => isSameShippingMethod(method, state.shippingMethod))
      || methods.find((method) => method['carrier_code'] === DEFAULT_SHIPPING_CARRIER)
      || (methods.length === 1 ? methods[0] : null);

    if (!selectedMethod) {
      commit('SET_SHIPPING_METHOD', {});
      appSessionStorage.setItem(SELECTED_SHIPPING_METHOD_KEY, {});
      return;
    }

    await dispatch('setShippingMethod', selectedMethod);
  },
  async setShippingCountries({commit}, payload) {
    try {
      commit('SET_SHIPPING_COUNTRIES', payload);
    } catch (err) {
      //
    }
  },
};

const state = initialState();

const CheckoutShipping = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}

export default CheckoutShipping;
